import { destructureDate, toIsoDate, toShortDate } from '../../../util/shared/tmc-global';
import { amountFormat, amountToNumber, pIdFormat } from '../../../util/shared/vue-global';
import {
  exemptManufacturingStatusCode, getSitusAddress, penaltyCalculation,
  taxIsDelinquent, unpostedPaymentStatusCode,
} from './tax-common.util';

const handleColorChange = (tabName, list, tabs) => {
  const tabList = [...tabs];
  for (let i = 0; i < tabList.length; i += 1) {
    if (list.length > 0) {
      if (tabList[i].tab === tabName) tabList[i].color = '#008040';
    } else if (tabList[i].tab === tabName) tabList[i].color = '';
  }
  return tabList;
};

const makeCriteriaStateNullIfEmptyString = (criteriaState) => {
  const obj = { ...criteriaState };
  for (const [key, value] of Object.entries(obj)) {
    if (value === '') obj[key] = null;
  }
  return obj;
};

const mergeCodeWithName = (
  items, codeKey, nameKey, doesItNeedEmptySelect, ignoreCodeAdditionToName,
) => {
  const updatedItems = [];
  if (doesItNeedEmptySelect) updatedItems.push({ name: '', value: null });
  for (let i = 0; i < (items || []).length; i += 1) {
    let filteredItem = '';
    const nameKeyVal = items[i][nameKey] ? items[i][nameKey] : '';
    const codeKeyVal = items[i][codeKey] ? items[i][codeKey] : '';
    const checkNameExitsOrNot = nameKeyVal ? ` - ${nameKeyVal}` : '';
    if (ignoreCodeAdditionToName) filteredItem = nameKeyVal;
    else filteredItem = `${codeKeyVal} ${checkNameExitsOrNot}`;
    updatedItems.push({ name: filteredItem, value: codeKeyVal });
  }
  return updatedItems;
};

const commonFuncForFilterMtgCoProc = (itemData, subProgramName) => {
  const filteredTaxItemData = itemData.length > 0
    ? itemData.filter((fil) => fil.subProgram === subProgramName) : [];
  const mapTaxItemData = filteredTaxItemData.length > 0
    ? filteredTaxItemData.map((fil) => fil.name) : [];
  return { filteredTaxItemData, mapTaxItemData };
};

const getPreviousMonthDate = (date = new Date()) => {
  let current;
  const curDate = new Date(date);
  const selectedMonth = curDate.getMonth();
  const selectedDate = curDate.getDate();
  const selectedYear = curDate.getFullYear();
  // checking for month and if exists getting the previous month
  if (selectedMonth === 0) current = new Date(selectedYear - 1, 11, selectedDate + 1);
  else current = new Date(selectedYear, selectedMonth - 1, selectedDate + 1);
  return toIsoDate(current);
};

const getNextMonthDate = (date = new Date()) => {
  let current;
  const curDate = new Date(date);
  const selectedMonth = curDate.getMonth();
  const selectedDate = curDate.getDate();
  const selectedYear = curDate.getFullYear();
  // checking for month and if exists getting the previous month
  if (selectedMonth === 0) current = new Date(selectedYear, 1, selectedDate + 1);
  else current = new Date(selectedYear, selectedMonth + 1, selectedDate + 1);
  return toIsoDate(current);
};

/**
 * Converts MM-DD-YYYY string format to YYYY-MM-DD for proper UTC parsing
 * @param {string} dateStr - Date string in MM-DD-YYYY or YYYY-MM-DD format
 * @returns {string} - Date string in YYYY-MM-DD format
 */
const convertToIsoFormat = (dateStr) => {
  if (typeof dateStr !== 'string') return dateStr;
  const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(dateStr);
  if (match) {
    return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
  }
  return dateStr;
};

/**
 * Gets the previous month date using UTC methods to avoid timezone issues.
 * This is timezone-safe and should be used when dealing with dates across different timezones.
 * @param {Date|string} date - Input date (Date object or string in MM-DD-YYYY or YYYY-MM-DD format)
 * @returns {string} - ISO date string (YYYY-MM-DD) for the previous month
 */
const getPreviousMonthDateUTC = (date = new Date()) => {
  let dateStr = date;
  // If it's a string in MM-DD-YYYY format, convert to YYYY-MM-DD for proper UTC parsing
  if (typeof dateStr === 'string') {
    dateStr = convertToIsoFormat(dateStr);
  }
  const curDate = new Date(dateStr);
  const selectedMonth = curDate.getUTCMonth();
  const selectedDate = curDate.getUTCDate();
  const selectedYear = curDate.getUTCFullYear();

  let current;
  // checking for month and if January (0), go to December of previous year
  if (selectedMonth === 0) {
    current = new Date(Date.UTC(selectedYear - 1, 11, selectedDate));
  } else {
    current = new Date(Date.UTC(selectedYear, selectedMonth - 1, selectedDate));
  }
  return toIsoDate(current);
};

/**
 * Gets the next month date using UTC methods to avoid timezone issues.
 * This is timezone-safe and should be used when dealing with dates across different timezones.
 * @param {Date|string} date - Input date (Date object or string in MM-DD-YYYY or YYYY-MM-DD format)
 * @returns {string} - ISO date string (YYYY-MM-DD) for the next month
 */
const getNextMonthDateUTC = (date = new Date()) => {
  let dateStr = date;
  // If it's a string in MM-DD-YYYY format, convert to YYYY-MM-DD for proper UTC parsing
  if (typeof dateStr === 'string') {
    dateStr = convertToIsoFormat(dateStr);
  }
  const curDate = new Date(dateStr);
  const selectedMonth = curDate.getUTCMonth();
  const selectedDate = curDate.getUTCDate();
  const selectedYear = curDate.getUTCFullYear();

  let current;
  // checking for month and if December (11), go to January of next year
  if (selectedMonth === 11) {
    current = new Date(Date.UTC(selectedYear + 1, 0, selectedDate));
  } else {
    current = new Date(Date.UTC(selectedYear, selectedMonth + 1, selectedDate));
  }
  return toIsoDate(current);
};

/**
 * Rolls a date back to the last day of the previous penalty period, i.e. the mirror
 * image of BrowseTrWorkFileFooter's calculatePenaltyThrough. Lands on
 * (penaltyChangeDay - 1) of the CURRENT month if the input date is on/after the
 * change day, otherwise of the PREVIOUS month - so it always lands on a period
 * boundary instead of drifting back by a flat calendar month. UTC-safe via
 * destructureDate; accepts a Date object or a MM-DD-YYYY/YYYY-MM-DD string.
 * @param {Date|string} date - the date to roll back from
 * @param {number|string} penaltyChangeDay - the county's penalty period change day
 * @returns {string} - ISO date string (YYYY-MM-DD) of the previous period's end
 */
const getPreviousPenaltyPeriodEnd = (date = new Date(), penaltyChangeDay) => {
  const changeDay = Number(penaltyChangeDay);
  const { year, month, day } = destructureDate(date);
  const monthIndex = (month - 1) + (day >= changeDay ? 0 : -1);
  return toIsoDate(new Date(Date.UTC(year, monthIndex, changeDay - 1)));
};

const postProcessCommentsTrans = (items, commentsCategory) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = { ...items[i] };
    const removeNullVal = (commentsCategory || []).filter((gt) => !!gt.name && !!gt.value);
    const filCatName = (removeNullVal || []).filter((gt) => gt.value === item.category);
    item.categoryName = filCatName.length ? filCatName[0].name : '';
    if (item.category === 7) item.isItCategorySeven = true;
    updatedItems.push(item);
  }
  return updatedItems;
};

const taxFeeCheckWitheEfectiveDate = (taxRollFeesTable = [], todaysDate) => {
  let getAllFeeTotal = 0;
  // Normalize to UTC midnight to avoid local-time parsing differences
  // (MM-DD-YYYY parses as local midnight, YYYY-MM-DD as UTC midnight)
  const parsed = new Date(todaysDate);
  const curDate = new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate() + 1),
  );
  for (const ele of (taxRollFeesTable || [])) {
    const efeDate = new Date(ele.effectiveDate);

    // check if they are paid
    if (((efeDate < curDate) || !efeDate) && !ele.paymentNumber) {
      getAllFeeTotal += amountToNumber(ele.feeAmount);
    }
  }
  return getAllFeeTotal;
};

const taxCommonPostProcessTrans = (items, todaysDate, taxSettingItem,
  penaltyDateValue, pidValue = '', taxStatusCodes = {}, feesData = []) => {
  const updatedItems = [];
  const todaysPenDate = penaltyDateValue || toIsoDate(todaysDate);
  const { penaltyDate, penaltyRate } = taxSettingItem;
  for (let i = 0; i < items.length; i += 1) {
    const item = { ...items[i] };
    const isExemptManufacturing = (item.statusCode || []).includes(exemptManufacturingStatusCode);
    const itemFeeData = feesData || [];
    let totalFees = item.totalFee || 0;
    if (itemFeeData.length && !isExemptManufacturing) {
      // to get fee based on effective date
      const feeList = itemFeeData.filter((ft) => ft.taxMongoId === item._id);
      totalFees = taxFeeCheckWitheEfectiveDate(feeList, todaysPenDate);
    }
    const returnedVal = penaltyCalculation(
      todaysPenDate, item.penaltyStartDate, penaltyDate,
      item.totalTax, item.totalPaid,
      totalFees, penaltyRate, item.totalDue,
      item.taxYear, (item.statusCode || []),
      taxSettingItem,
    );

    const highlightColors = [];
    if (item.statusCode && item.statusCode.length) {
      // this is to highlight the reecords based on the status codes
      // the priority of to highlight is 400 and above and then rest
      const toNum = (c) => Number(c);
      // sort the status codes by 400 and above and then rest
      const fourHundredAndAbove = item.statusCode.filter((c) => toNum(c) >= 400)
        .sort((a, b) => toNum(a) - toNum(b));
      // sort the status codes by less than 400
      const rest = item.statusCode.filter((c) => toNum(c) < 400)
        .sort((a, b) => toNum(a) - toNum(b));
      const sortedStatusCodes = [...fourHundredAndAbove, ...rest];
      // get the highlight color for the status codes
      for (const statusCode of (sortedStatusCodes || [])) {
        if (taxStatusCodes[statusCode]) {
          highlightColors.push(taxStatusCodes[statusCode]);
        }
      }
    }
    if (highlightColors.length) {
      const firstHighlightColor = highlightColors[0];
      // set the highlight color for the item
      item[firstHighlightColor] = true;
    }
    const totalDueWithPenAndfee = item.totalDue
    + amountToNumber(returnedVal.penaltyWithOrWithoutFees);
    // item.feesAndPen = amountFormat(item.totalDue + returnedVal.penaltyWithOrWithoutFees);
    item.feesAndPen = amountFormat(totalDueWithPenAndfee);
    item.getPenRateWithMonths = returnedVal.getPenRateWithMonths;
    item.penaltyAmount = returnedVal.penaltyAmt;

    item.penaltyDateValue = penaltyDateValue;
    item.penaltyStartDate = item.penaltyStartDate ? toShortDate(item.penaltyStartDate)
      : item.penaltyStartDate;
    item.mhDecalDate = item.mhDecalDate ? toShortDate(item.mhDecalDate)
      : item.mhDecalDate;
    // common filter for 0 values
    item.lot = item.lot === 0 ? null : item.lot;
    item.block = item.block === '0' ? null : item.block;
    item.lots = item.lots === 0 ? null : item.lots;
    item.acres = item.acres === 0 ? null : item.acres;
    item.situsTown = item.situsTown === '0' ? null : item.situsTown;
    item.additionDesc = item.additionDesc === '0' ? null : item.additionDesc;
    item.section = item.section === '0' ? null : item.section;
    item.situsStreetNumber = item.situsStreetNumber === 0 ? null : item.situsStreetNumber;
    item.situsAddress = getSitusAddress(item);
    // if only totalDue is less than 0 change the color, to show overpaid
    item.totalDueIsNegative = item.totalDue < -0.01;
    updatedItems.push(item);

    item.mhPropertyOrParcelId = pIdFormat(item.mhPropertyOrParcelId, pidValue);
    item.pIdOfRealEstate = pIdFormat(item.pIdOfRealEstate, pidValue);
    item.propertyOrParcelId = pIdFormat(item.propertyOrParcelId, pidValue);
  }
  return updatedItems;
};

const penaltyCaclCrudListCommonFunc = (
  val, item, taxSettingItem, itemFeeData = [], fromWhere,
) => {
  const { penaltyDate, penaltyRate } = taxSettingItem;
  const itemData = [...item];
  const formattedArray = [];
  for (let i = 0; i < (itemData || []).length; i += 1) {
    let totalFees = itemData[i].totalFee;

    // if status code is 406, then return 0 for penalty and also for fee
    const isExemptManufacturing = (itemData[i].statusCode || [])
      .includes(exemptManufacturingStatusCode);
      // to get fee based on effective date
    const id = itemData[i]._id;
    let feeList = [];
    if (fromWhere === 'fromPickList') {
      feeList = itemData[i].feeList;
    } else {
      feeList = (itemFeeData || []).filter((ft) => ft.taxMongoId === id);
    }

    if (isExemptManufacturing) totalFees = 0;
    else totalFees = taxFeeCheckWitheEfectiveDate(feeList, val);

    const returnedVal = penaltyCalculation(
      val, itemData[i].penaltyStartDate, penaltyDate,
      itemData[i].totalTax, itemData[i].totalPaid,
      totalFees, penaltyRate, itemData[i].totalDue,
      itemData[i].taxYear, (itemData[i].statusCode || []),
      taxSettingItem,
    );
    itemData[i].feesAndPen = amountFormat(itemData[i].totalDue
       + returnedVal.penaltyWithOrWithoutFees);
    itemData[i].getPenRateWithMonths = returnedVal.getPenRateWithMonths;
    itemData[i].penaltyAmount = returnedVal.penaltyAmt;
    itemData[i].penaltyDateValue = val;
    itemData[i].totalFee = totalFees;
    formattedArray.push(itemData[i]);
  }
  return formattedArray;
};

const taxPostProcessFeesList = (items) => {
  const updatedItems = [];
  for (let i = 0; i < (items.length || []); i += 1) {
    const ele = items[i];
    ele.taxMongoId = ele.taxInquiryTransaction._id;
    updatedItems.push(ele);
  }
  return updatedItems;
};

const getFeeListBasedOnEffeciveDate = (feeItems, todaysDate) => {
  const feeDet = [];
  let feesAmt = 0;
  const curDate = new Date(todaysDate);
  for (let i = 0; i < (feeItems.length || []); i += 1) {
    const ele = feeItems[i];
    const efeDate = new Date(ele.effectiveDate);
    if ((efeDate <= curDate) || !efeDate) {
      feeDet.push({
        feeDescription: ele.feeDescription,
        feeNumber: ele.feeNumber,
        feeMongoId: ele._id,
        feeAmount: ele.feeAmount,
      });
      feesAmt += amountToNumber(ele.feeAmount);
    }
  }
  return { feeDet, feesAmt };
};

const delQRecords = {
  _id: '',
  propertyOrParcelId: '',
  taxYear: '',
  taxId: '',
  totalDue: '',
  totalPaid: '',
  totalTax: '',
  penaltyStartDate: '',
  totalFee: '',
  feesAndPen: '',
  statusCode: [],
};

const buildPostProcessForDelinquant = (items, taxSettingItem) => {
  const delinquentTaxRecords = [];
  const { penaltyDate, penaltyRate, pidValue } = taxSettingItem;
  const todaysPenDate = toIsoDate(new Date());
  for (const value of items) {
    const isDelq = taxIsDelinquent(value, penaltyDate);
    if (isDelq) {
      const item = { ...value };
      const returnedVal = penaltyCalculation(
        todaysPenDate, item.penaltyStartDate, penaltyDate,
        item.totalTax, item.totalPaid,
        item.totalFee, penaltyRate, item.totalDue,
        item.taxYear, (item.statusCode || []),
      );
      item.taxAmountDue = amountFormat(item.totalDue);
      item.feesAndPen = amountFormat(item.totalDue
        + amountToNumber(returnedVal.penaltyWithOrWithoutFees));
      item.hasPendingPayment = (item.statusCode || []).includes(unpostedPaymentStatusCode);
      item.propertyOrParcelId = pIdFormat(item.propertyOrParcelId, pidValue);
      delinquentTaxRecords.push(item);
    }
  }
  return delinquentTaxRecords;
};

const taxFeesPostProcessTrans = (items) => {
  const updatedItems = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = { ...items[i] };
    item.effectiveDate = item.effectiveDate ? toShortDate(item.effectiveDate)
      : item.effectiveDate;
    item.paymentDate = item.paymentDate ? toShortDate(item.paymentDate)
      : item.paymentDate;
    updatedItems.push(item);
  }
  return updatedItems;
};

const taxReasonListDropDownPostProcessTrans = (items) => {
  const updatedItems = mergeCodeWithName(items, 'reasonCode', 'description', true);
  return updatedItems;
};

const taxInquiryCriteriaFilter = (criteriaState) => {
  const returnCriteria = makeCriteriaStateNullIfEmptyString(criteriaState);
  // for baseId its has to sorted by taxYear for others its taxId
  // for baseId is not there and selected all taxyear then use 'sortByAllTaxYear'
  const sortByTaxYear = [{ column: 'taxYear', direction: 'desc' }];
  const sortByAllTaxYear = [{ column: 'taxYear', direction: 'desc' }, { column: 'taxId', direction: 'asc' }];
  const sortByTaxId = [{ column: 'taxId', direction: 'asc' }];
  const sortBySitusStreetName = [{ column: 'situsStreetName', direction: 'asc' }];
  if (returnCriteria.situsStreetNumber && returnCriteria.situsStreetDirection
    && !returnCriteria.situsStreetName) {
    returnCriteria.sortBy = sortBySitusStreetName;
  } else if (returnCriteria.baseId) returnCriteria.sortBy = sortByTaxYear;
  else if (!returnCriteria.taxYear && !returnCriteria.baseId) {
    returnCriteria.sortBy = sortByAllTaxYear;
  } else returnCriteria.sortBy = sortByTaxId;
  return returnCriteria;
};

const isSameDate = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear()
     && d1.getMonth() === d2.getMonth()
     && d1.getDate() === d2.getDate();
};

const getTwoThirdsAssessed = (mainAssessedValue, mainTotalFeesDue) => {
  // this 0.667 calculated value of 2/3;
  const twoThirdsAssessed = (amountToNumber(mainAssessedValue || 0) * 0.667)
   + amountToNumber(mainTotalFeesDue || 0);
  return twoThirdsAssessed || 0;
};

const juneResaleCommonPostProcessTrans = (items, pidValue = '') => {
  const alteredItems = [];
  for (let h = 0; h < (items || []).length; h += 1) {
    let totalPenalty = 0;
    const selItems = items[h];
    for (let i = 0; i < (selItems.taxDetails || []).length; i += 1) {
      const selItem = selItems.taxDetails[i];
      selItem.systemId = selItems.systemId;
      selItem.propertyOrParcelId = pIdFormat(selItem.propertyOrParcelId, pidValue);
      selItem.taxType = (selItem.taxType || '').toUpperCase();
      totalPenalty += amountToNumber(selItem.currentPenalty);
    }
    selItems.mainPropertyOrParcelId = pIdFormat(selItems.mainPropertyOrParcelId, pidValue);
    selItems.mainTotalDue = amountFormat(selItems.mainTotalDue);
    selItems.winningBid = amountFormat(selItems.winningBid);
    selItems.mainTotalPenalty = totalPenalty;
    selItems.mainTwoThirdsAssessed = getTwoThirdsAssessed(
      selItems.mainAssessedValue, selItems.mainTotalFeesDue,
    );
    alteredItems.push(selItems);
  }
  return alteredItems;
};

const commissionersSaleCommonPostProcessTrans = (items, pidValue = '') => {
  const alteredItems = [];
  for (let h = 0; h < (items || []).length; h += 1) {
    const selItems = items[h];
    selItems.propertyOrParcelId = pIdFormat(selItems.propertyOrParcelId, pidValue);
    alteredItems.push(selItems);
  }
  return alteredItems;
};

/**
 * Compares original and current item, returning only the fields that changed.
 * Always includes _id for record identification.
 */
const getChangedFields = (original, current) => {
  const changes = {};
  const allKeys = new Set([...Object.keys(original || {}), ...Object.keys(current || {})]);
  allKeys.forEach((key) => {
    const origVal = (original || {})[key];
    const currVal = (current || {})[key];
    if (Array.isArray(origVal) || Array.isArray(currVal)) {
      if (JSON.stringify(origVal) !== JSON.stringify(currVal)) {
        changes[key] = currVal;
      }
    } else if (origVal !== currVal) {
      changes[key] = currVal;
    }
  });
  return changes;
};

export {
  buildPostProcessForDelinquant, commonFuncForFilterMtgCoProc, delQRecords,
  getChangedFields,
  getFeeListBasedOnEffeciveDate, getNextMonthDate, getNextMonthDateUTC,
  getPreviousMonthDate, getPreviousMonthDateUTC, getPreviousPenaltyPeriodEnd,
  handleColorChange, isSameDate, juneResaleCommonPostProcessTrans,
  makeCriteriaStateNullIfEmptyString,
  mergeCodeWithName, penaltyCaclCrudListCommonFunc, postProcessCommentsTrans,
  taxCommonPostProcessTrans, taxFeeCheckWitheEfectiveDate, taxFeesPostProcessTrans,
  taxInquiryCriteriaFilter, taxPostProcessFeesList, taxReasonListDropDownPostProcessTrans,
  commissionersSaleCommonPostProcessTrans,
};
